import { PERSIST_DATA_LOAD_COMPLETE } from 'app-components/flux/core-actions/coreActions';
import { getLogger } from 'app-components/utils/core';
import { tracker } from 'app-components/utils/core';
import { immutableSerialize } from 'app-components/utils/redux-store';
import { type SagaIterator, buffers } from 'redux-saga';
import { actionChannel, call, delay, flush, fork, race, select, take } from 'redux-saga/effects';
import { setCacheMetadata } from './cacheMetadata';
import { type Config, config, defaultGetCacheKey } from './config';
import { encryptString } from './encryption';
const log = getLogger('persist-sagas.ts');

const pattern = '*';

export const defaultStateToCache = <D>(data: D) => immutableSerialize.convertToJSRecursive(data);

export function* persist(lastState: any, nextState: any, config: Config) {
  yield fork(setCacheMetadata, config);

  for (const key of config.stateKeys) {
    if (lastState?.[key] !== nextState?.[key]) {
      log.debug(`save to persist ${key}`);

      const reducerConfig = config.persistableStateConfig[key];

      const { stateToCache = defaultStateToCache } = reducerConfig;

      const wrapped = {
        version: reducerConfig.version,
        data: stateToCache(nextState[key]),
      };

      const serialized = JSON.stringify(wrapped);

      let itemToStore = serialized;

      const shouldEncrypt = Boolean(reducerConfig.encrypt);

      if (shouldEncrypt) {
        itemToStore = encryptString(serialized, config.encryptionKey);
      }

      const getCacheKey = config.getCacheKeyForStateKey || defaultGetCacheKey;

      if (config?.storageInterface) {
        try {
          yield fork([config.storageInterface, 'setItem'], getCacheKey(key), itemToStore);
        } catch (e) {
          log.error('Storage interface error: ', e);
          tracker.track('storageInterfaceFailure', e);
        }
      }

      if (reducerConfig.onChange) {
        yield fork(reducerConfig.onChange, lastState, nextState);
      }
    }
  }
}

// Modified version of the built-in debounce effect documented here:
// https://redux-saga.js.org/docs/api#notes-15
// The built-in debounce forks the child task, but we shouldn't allow overlapping
// calls to persist the same state keys. Instead we wait while persisting and
// use an action channel to check if the state changed while we were persisting.
// The built-in debounce task also doesn't give us a way to maintain a reference
// to 'lastState' that we can pass into the child task.
export function* persistOnStateChangeDebounced(): SagaIterator {
  yield take(PERSIST_DATA_LOAD_COMPLETE);

  let lastState = yield select();

  const channel = yield actionChannel(pattern, buffers.sliding(1));

  while (true) {
    yield take(channel);

    while (true) {
      const { debounced, action } = yield race({
        debounced: delay(1000),
        action: take(pattern),
      });

      yield flush(channel);

      if (debounced || action.type === 'AUTH_PERSIST_AUTH_SESSION' || action.type === 'AUTH_SELECT_DATASET') {
        const nextState = yield select();

        if (lastState !== nextState) {
          try {
            if (!config) {
              // There are scenarios where we intentionally don't initialize the config to prevent persisting (waiting until the user is logged in on web for example)
              // it's not necessarily an error if the config is not yet initialized.
              log.log('Persist config is not initialized. State cannot be persisted.');

              break;
            }

            const start = new Date();

            yield call(persist, lastState, nextState, config);

            const end = new Date();

            log.log(`save to persist complete in ${Number(end) - Number(start)}ms`);
          } catch (e) {
            log.error('Error persisting state to cache: ', e);

            break;
          }

          lastState = nextState;
        }

        break;
      }
    }
  }
}

export default [persistOnStateChangeDebounced];
